import { createContext, useState, useContext, useEffect, useCallback, useMemo} from 'react';
import { useParams } from 'react-router-dom';
import { api } from "../config";
import { useNavigate } from "react-router-dom";
const RotaContext = createContext();

export const useRota = () => useContext(RotaContext);

export const RotaProvider = ({ children }) => {
    const navigate = useNavigate();
    const { idFeira } = useParams();

    const [activeFilters, setActiveFilters] = useState({});
    const [selectedExpositor, setSelectedExpositor] = useState(null);
    
    const [estado, setEstado] = useState({
        feira: null,
        expositores: [],
        filteredExpositores: [],
        mapData: null,
        fundoUrl: '',
        tilesUrl: '',
        tilesOptions: {},
        tilesStatus: {},
        imagemFundo: '',
        estandesCadastrados: [],
        tiposEspacoFeira: [],
        nodes: [],
        edges: [],
        regras: [],
        setoresFeira: [],
        categoriasFeira: [],
        isLoading: true,
        error: null
    });

    const carregarDados = useCallback(async () => {
        if (!idFeira) {
            setEstado(prev => ({ ...prev, error: "ID não encontrado", isLoading: false }));
            return;
        }

        try {
            const urls = [
                `${api.API_URL}/rotaFeira/${idFeira}`,
                `${api.API_URL}/rota/mapa/${idFeira}`,
                `${api.API_URL}/rota/estandes/${idFeira}`,
                `${api.API_URL}/feira/tiposEspaco/${idFeira}`,
                `${api.API_URL}/rota/caminhos/${idFeira}`,
                `${api.API_URL}/categoria/${idFeira}`,
                `${api.API_URL}/setor/${idFeira}`,
                `${api.API_URL}/rota/regras-fluxo/${idFeira}`,
            ];

            const respostas = await Promise.all(urls.map(url => fetch(url)));
            const [dados, dataMapa, dataEstandes, dataTipos, dataGrafo, dataCats, dataSets, dataRegrasRota] = 
                await Promise.all(respostas.map(r => r.json()));

            const { expositores, ...feiraSemExpositores } = dados.retorno.dados;

            let nodesAdaptados = [];
            let edgesAdaptados = [];
            if (dataGrafo.status && dataGrafo.data) {
                nodesAdaptados = dataGrafo.data.nodes.map(nodeDB => ({
                    id: nodeDB.id, x: nodeDB.posicao_x, y: nodeDB.posicao_y,
                    type: nodeDB.tipo, name: nodeDB.nome || nodeDB.descricao,
                    tipo_espaco_id: nodeDB.tipo_espaco_id
                }));
                edgesAdaptados = (dataGrafo.data.edges || []).map(edgeDB => ({
                    id: `edge_${edgeDB.no_origem_id}_${edgeDB.no_destino_id}`,
                    source: edgeDB.no_origem_id, target: edgeDB.no_destino_id, weight: edgeDB.peso
                }));
            }
            
            if (!dados.retorno.dados.habilitada_rotas) {
                navigate("/mapa-desativado");
                return;
            }

            setEstado({
                feira: feiraSemExpositores,
                expositores: expositores || [],
                filteredExpositores: dados.retorno.dados.filteredExpositores || [],
                mapData: dataMapa.data?.mapa?.mapa || null,
                imagemFundo: dataMapa.data?.fundo,
                fundoUrl: dataMapa.data?.fundoURL || "",
                tilesUrl: dataMapa.data?.tilesTemplateURL || "",
                tilesStatus: dataMapa.data?.tiles_status || {},
                tilesOptions: {
                    tile_format: dataMapa.data?.tile_format,
                    tile_max_zoom: dataMapa.data?.tile_max_zoom,
                    tile_size: dataMapa.data?.tile_size,
                },
                estandesCadastrados: dataEstandes.data || [],
                tiposEspacoFeira: dataTipos.data || [],
                nodes: nodesAdaptados,
                edges: edgesAdaptados,
                regras: Array.isArray(dataRegrasRota.data) ? dataRegrasRota.data : [],
                categoriasFeira: dataCats.data || [],
                setoresFeira: dataSets.data || [],
                isLoading: false,
                error: null
            });

        } catch (err) {
            setEstado(prev => ({ ...prev, error: err.message, isLoading: false }));
        }
    }, [idFeira]);

    useEffect(() => {
        carregarDados();
    }, [carregarDados]);

    const selectExpositor = (expositor) => {
        setSelectedExpositor(expositor);
    };

    const clearSelectedExpositor = () => {
        setSelectedExpositor(null);
    };

    const applyFilters = (newFilters) => {
        setActiveFilters(prev => ({...prev, ...newFilters}));
    };
    
    const clearFilters = () => {
        setActiveFilters({});
    }

    const value = useMemo(() => ({
        ...estado,
        selectExpositor,
        clearSelectedExpositor,
        applyFilters,
        clearFilters
    }), [
        estado,
        selectExpositor, clearSelectedExpositor, applyFilters, clearFilters
    ]);

    return (
        <RotaContext.Provider value={value}>
            {children}
        </RotaContext.Provider>
    );
};